import { create } from "zustand";
import { fetchApi } from "@/lib/api";
import { captureException } from "@/lib/analytics";
import {
  CreateStandardAgentTriggerBody,
  StandardAgentTrigger,
  StandardAgentTriggerResponseSchema,
  StandardAgentTriggersResponseSchema,
  UpdateStandardAgentTriggerBody,
} from "@forgepoint/shared";

const LIST_STATE_KEY = "__list__";

interface TriggerLoadingState {
  isLoadingGet: boolean;
  isLoadingSave: boolean;
  isLoadingDelete: boolean;
  getError: string | null;
  saveError: string | null;
  deleteError: string | null;
}

interface StandardAgentTriggersStore {
  triggerMap: Map<string, StandardAgentTrigger>;
  triggerIds: string[];
  triggerStateMap: Map<string, TriggerLoadingState>;
  fetchTriggers: () => Promise<void>;
  createTrigger: (
    payload: CreateStandardAgentTriggerBody,
  ) => Promise<StandardAgentTrigger | null>;
  updateTrigger: (
    triggerId: string,
    payload: UpdateStandardAgentTriggerBody,
  ) => Promise<StandardAgentTrigger | null>;
  deleteTrigger: (triggerId: string) => Promise<boolean>;
  getTriggerById: (triggerId: string) => StandardAgentTrigger | undefined;
  getTriggerState: (key: string) => TriggerLoadingState;
}

const getDefaultTriggerLoadingState = (): TriggerLoadingState => ({
  isLoadingGet: false,
  isLoadingSave: false,
  isLoadingDelete: false,
  getError: null,
  saveError: null,
  deleteError: null,
});

const setTriggerState = (
  stateMap: Map<string, TriggerLoadingState>,
  key: string,
  partial: Partial<TriggerLoadingState>,
): Map<string, TriggerLoadingState> => {
  const nextStateMap = new Map(stateMap);
  const existing = nextStateMap.get(key) ?? getDefaultTriggerLoadingState();
  nextStateMap.set(key, { ...existing, ...partial });
  return nextStateMap;
};

const getTriggerIdsFromMap = (
  triggerMap: Map<string, StandardAgentTrigger>,
): string[] => {
  return Array.from(triggerMap.values())
    .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime())
    .map((trigger) => trigger.id);
};

const toError = (error: unknown): Error => {
  if (error instanceof Error) {
    return error;
  }
  return new Error("Unknown error");
};

export const useStandardAgentTriggersStore = create<StandardAgentTriggersStore>(
  (set, get) => ({
    triggerMap: new Map(),
    triggerIds: [],
    triggerStateMap: new Map([
      [LIST_STATE_KEY, getDefaultTriggerLoadingState()],
    ]),

    fetchTriggers: async () => {
      set((state) => ({
        triggerStateMap: setTriggerState(
          state.triggerStateMap,
          LIST_STATE_KEY,
          {
            isLoadingGet: true,
            getError: null,
          },
        ),
      }));

      try {
        const response = await fetchApi("standard-agent/triggers", "GET");
        if (!response.ok) {
          throw new Error("Failed to fetch standard agent triggers");
        }

        const parsedResponse = StandardAgentTriggersResponseSchema.safeParse(
          response.data,
        );

        if (!parsedResponse.success) {
          throw new Error("Invalid standard agent triggers response");
        }

        const triggerMap = new Map<string, StandardAgentTrigger>();
        parsedResponse.data.triggers.forEach((trigger) => {
          triggerMap.set(trigger.id, trigger);
        });

        set((state) => ({
          triggerMap,
          triggerIds: getTriggerIdsFromMap(triggerMap),
          triggerStateMap: setTriggerState(
            state.triggerStateMap,
            LIST_STATE_KEY,
            {
              isLoadingGet: false,
              getError: null,
            },
          ),
        }));
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to fetch standard agent triggers";
        captureException(toError(error));
        set((state) => ({
          triggerStateMap: setTriggerState(
            state.triggerStateMap,
            LIST_STATE_KEY,
            {
              isLoadingGet: false,
              getError: errorMessage,
            },
          ),
        }));
      }
    },

    createTrigger: async (payload) => {
      set((state) => ({
        triggerStateMap: setTriggerState(
          state.triggerStateMap,
          LIST_STATE_KEY,
          {
            isLoadingSave: true,
            saveError: null,
          },
        ),
      }));

      try {
        const response = await fetchApi("standard-agent/triggers", "POST", {
          body: payload,
        });
        if (!response.ok) {
          throw new Error("Failed to create standard agent trigger");
        }

        const parsedResponse = StandardAgentTriggerResponseSchema.safeParse(
          response.data,
        );

        if (!parsedResponse.success) {
          throw new Error("Invalid standard agent trigger response");
        }

        const createdTrigger = parsedResponse.data.trigger;

        set((state) => {
          const triggerMap = new Map(state.triggerMap);
          triggerMap.set(createdTrigger.id, createdTrigger);
          return {
            triggerMap,
            triggerIds: getTriggerIdsFromMap(triggerMap),
            triggerStateMap: setTriggerState(
              state.triggerStateMap,
              LIST_STATE_KEY,
              {
                isLoadingSave: false,
                saveError: null,
              },
            ),
          };
        });

        return createdTrigger;
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to create standard agent trigger";
        captureException(toError(error));
        set((state) => ({
          triggerStateMap: setTriggerState(
            state.triggerStateMap,
            LIST_STATE_KEY,
            {
              isLoadingSave: false,
              saveError: errorMessage,
            },
          ),
        }));
        return null;
      }
    },

    updateTrigger: async (triggerId, payload) => {
      set((state) => ({
        triggerStateMap: setTriggerState(state.triggerStateMap, triggerId, {
          isLoadingSave: true,
          saveError: null,
        }),
      }));

      try {
        const response = await fetchApi(
          `standard-agent/triggers/${triggerId}`,
          "PATCH",
          {
            body: payload,
          },
        );
        if (!response.ok) {
          throw new Error("Failed to update standard agent trigger");
        }

        const parsedResponse = StandardAgentTriggerResponseSchema.safeParse(
          response.data,
        );
        if (!parsedResponse.success) {
          throw new Error("Invalid standard agent trigger response");
        }

        const updatedTrigger = parsedResponse.data.trigger;

        set((state) => {
          const triggerMap = new Map(state.triggerMap);
          triggerMap.set(updatedTrigger.id, updatedTrigger);
          return {
            triggerMap,
            triggerIds: getTriggerIdsFromMap(triggerMap),
            triggerStateMap: setTriggerState(state.triggerStateMap, triggerId, {
              isLoadingSave: false,
              saveError: null,
            }),
          };
        });

        return updatedTrigger;
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to update standard agent trigger";
        captureException(toError(error));
        set((state) => ({
          triggerStateMap: setTriggerState(state.triggerStateMap, triggerId, {
            isLoadingSave: false,
            saveError: errorMessage,
          }),
        }));
        return null;
      }
    },

    deleteTrigger: async (triggerId) => {
      set((state) => ({
        triggerStateMap: setTriggerState(state.triggerStateMap, triggerId, {
          isLoadingDelete: true,
          deleteError: null,
        }),
      }));

      try {
        const response = await fetchApi(
          `standard-agent/triggers/${triggerId}`,
          "DELETE",
        );
        if (!response.ok) {
          throw new Error("Failed to delete standard agent trigger");
        }

        set((state) => {
          const triggerMap = new Map(state.triggerMap);
          triggerMap.delete(triggerId);
          return {
            triggerMap,
            triggerIds: getTriggerIdsFromMap(triggerMap),
            triggerStateMap: setTriggerState(state.triggerStateMap, triggerId, {
              isLoadingDelete: false,
              deleteError: null,
            }),
          };
        });

        return true;
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to delete standard agent trigger";
        captureException(toError(error));
        set((state) => ({
          triggerStateMap: setTriggerState(state.triggerStateMap, triggerId, {
            isLoadingDelete: false,
            deleteError: errorMessage,
          }),
        }));
        return false;
      }
    },

    getTriggerById: (triggerId) => {
      return get().triggerMap.get(triggerId);
    },

    getTriggerState: (key) => {
      return get().triggerStateMap.get(key) ?? getDefaultTriggerLoadingState();
    },
  }),
);

export { LIST_STATE_KEY };
